import { UseFormReturn } from 'react-hook-form';
import { FormTextField, Tab, Tabs, Typography } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { CountryField } from '../../Field/CountryField';
import { useAccountProductFiatQuery, useAccountsQuery } from '../../../api/accounts';
import { useCallback, useEffect, useMemo } from 'react';
import { CurrencyField } from '../../Field/CurrencyField';
import {
  useExternalOutgoingFiatNetwork,
  useExternalOutgoingProviderQuery,
} from '../../../api/transfers';
import { BankBeneficiaryFormValues } from './types';
import { RoutingNumberField } from '../../Field/RoutingNumberField';
import { AccountNumberField } from '../../Field/AccountNumberField';
import { useFormError } from '../../../hooks/useFormError';
import { useBankInfo } from '../../../api/beneficiaries';
import { useBusinessQuery } from '../../../api/businesses';
import { ThirdPartyAlert } from './ThirdPartyAlert';
import { uniq } from 'lodash-es';
import { isFiatCurrency } from '@razz-team/utils';
import { BANK_TRANSFER_COUNTRY_CODES } from '../../../constants/country';

enum BankBeneficiaryType {
  business,
  individual,
}

export function BankBeneficiaryFields({
  form,
}: {
  form: UseFormReturn<BankBeneficiaryFormValues>;
}) {
  const { t } = useTranslation();

  const type = form.watch('beneficiary.valueType');
  const country = form.watch('beneficiary.value.bankCountry');
  const currency = form.watch('beneficiary.value.currency');
  const businessName = form.watch('beneficiary.value.businessName');

  const businessQuery = useBusinessQuery();
  const providerQuery = useExternalOutgoingProviderQuery(
    currency && country ? { currency, country } : undefined,
  );

  const handleBeneficiaryTypeChange = useCallback(
    (_: unknown, nextType: BankBeneficiaryType) => form.setValue('beneficiary.valueType', nextType),
    [form],
  );

  // const beneficiary = form.watch('beneficiary.value');
  // const beneficiaryValidateQuery = useValidateBeneficiary(beneficiary);
  // useFormError(form, 'beneficiary.value.businessName', beneficiaryValidateQuery.error);
  // useFormError(form, 'beneficiary.value.individualName', beneficiaryValidateQuery.error);

  const networkQuery = useExternalOutgoingFiatNetwork(
    currency && country ? { currency, country } : undefined,
  );

  const isThirdParty = businessName && businessName !== businessQuery.data?.business.name;
  const showThirdPartyBanner =
    __BRIGHTY__ &&
    isThirdParty &&
    providerQuery.isSuccess &&
    providerQuery.data?.provider !== 'BREINROCK';

  const accountsQuery = useAccountsQuery();
  const fiatProductQuery = useAccountProductFiatQuery();
  const currencies = useMemo(
    () =>
      uniq([
        ...(accountsQuery.data?.map((it) => it.balance.currency).filter(isFiatCurrency) ?? []),
        ...(fiatProductQuery.data?.product?.conditions.map((c) => c.currency) ?? []),
      ]),
    [accountsQuery.data, fiatProductQuery.data],
  );

  const countryError = networkQuery.error ?? providerQuery.error;
  useFormError(form, 'beneficiary.value.bankCountry', countryError);

  const routingNumber = form.watch('beneficiary.value.routingNumber');
  const bankInfoQuery = useBankInfo(routingNumber);
  useEffect(() => {
    const bicInfo = bankInfoQuery.data;
    if (bicInfo) {
      if (!form.getFieldState('beneficiary.value.bankCountry').isTouched) {
        form.setValue('beneficiary.value.bankCountry', bicInfo.country);
      }
      if (!form.getFieldState('beneficiary.value.bankName').isTouched) {
        form.setValue('beneficiary.value.bankName', bicInfo.bankName);
      }
      if (!form.getFieldState('beneficiary.value.bankAddress').isTouched) {
        form.setValue(
          'beneficiary.value.bankAddress',
          [bicInfo.streetLine, bicInfo.city, bicInfo.zipCode].filter(Boolean).join(', '),
        );
      }
    }
  }, [bankInfoQuery.data, form]);

  return (
    <>
      <Tabs value={type} onChange={handleBeneficiaryTypeChange}>
        <Tab label={t('Business')} value={BankBeneficiaryType.business} />
        <Tab label={t('Individual')} value={BankBeneficiaryType.individual} />
      </Tabs>
      <CurrencyField
        name="beneficiary.value.currency"
        label={t('Currency')}
        options={currencies}
        loading={fiatProductQuery.isPending}
        autoFocus
        required
        disableClearable
      />
      <CountryField
        name="beneficiary.value.bankCountry"
        label={t('Country of recipient’s bank')}
        options={BANK_TRANSFER_COUNTRY_CODES}
        required
        disableClearable
      />
      <RoutingNumberField
        name="beneficiary.value.routingNumber"
        required
        format={networkQuery.data?.routingNumber}
      />
      {networkQuery.data?.routingNumber2 ? (
        <RoutingNumberField
          name="beneficiary.value.routingNumber2"
          format={networkQuery.data?.routingNumber2}
          secondary
          fullWidth
        />
      ) : null}
      <AccountNumberField
        name="beneficiary.value.accountNumber"
        required
        format={networkQuery.data?.accountNumber}
      />
      {type === BankBeneficiaryType.individual ? (
        <FormTextField
          name="beneficiary.value.individualName"
          label={t('Beneficiary name (Individual)')}
          required
          autoComplete="off"
          pattern={{
            value: /^[a-zA-Z \.-–]*$/,
            message: t('Only letters, spaces ".", "-", "–" allowed'),
          }}
          minLength={2}
          maxLength={100}
        />
      ) : null}
      {type === BankBeneficiaryType.business ? (
        <FormTextField
          name="beneficiary.value.businessName"
          label={t('Beneficiary name (Company)')}
          required
          autoComplete="off"
          minLength={2}
          maxLength={100}
        />
      ) : null}
      {showThirdPartyBanner ? <ThirdPartyAlert /> : null}
      {networkQuery.data?.type === 'SWIFT' ? (
        <>
          <FormTextField
            name="beneficiary.value.bankName"
            label={t('Bank name')}
            required
            autoComplete="off"
          />
          <FormTextField
            name="beneficiary.value.bankAddress"
            label={t('Bank address')}
            required
            autoComplete="off"
          />
        </>
      ) : null}
      {providerQuery?.data?.beneficiaryAddressRequired ? (
        <>
          <Typography variant="h6">{t('Recipient address')}</Typography>
          <CountryField
            name="beneficiary.value.beneficiaryAddress.country"
            label={t('Recipient country')}
            required
            disableClearable
          />
          <FormTextField
            name="beneficiary.value.beneficiaryAddress.zipCode"
            label={t('Recipient postal code (optional)')}
            autoComplete="off"
            maxLength={50}
          />
          <FormTextField
            name="beneficiary.value.beneficiaryAddress.region"
            label={t('Recipient region (optional)')}
            autoComplete="off"
            maxLength={100}
          />
          <FormTextField
            name="beneficiary.value.beneficiaryAddress.city"
            label={t('Recipient city')}
            required
            autoComplete="off"
            maxLength={100}
          />
          <FormTextField
            name="beneficiary.value.beneficiaryAddress.streetLine1"
            label={t('Recipient address line 1')}
            required
            autoComplete="off"
            maxLength={100}
          />
          <FormTextField
            name="beneficiary.value.beneficiaryAddress.streetLine2"
            label={t('Recipient address line 2 (optional)')}
            autoComplete="off"
            maxLength={100}
          />
        </>
      ) : null}
    </>
  );
}
